import { useMemo } from 'react';

import {
	getUrlMaybe,
	type GlobalShortcutsItem,
	type UseGlobalShortcutDataOutput,
} from '@atlassian/global-shortcuts';

import { useNavAnalytics } from '../../../common/utils/use-nav-analytics';

type UseGlobalShortcutsTransformationPropsType = {
	growthShortcutsConfig: UseGlobalShortcutDataOutput;
	shortcutsConfig: UseGlobalShortcutDataOutput;
	visibleShortcuts: string[];
};

type GlobalShortcutsConfigWithMenuId = GlobalShortcutsItem & { menuItemId: string };

/**
 * Creates a transformation utility for global shortcuts and drag and drop customization
 * by creating a mirrored structure of the original shortcuts configuration containing a
 * menu identifier reference that can be used for drag and drop operations.
 */
export const useGlobalShortcutsTransformation = ({
	shortcutsConfig,
	growthShortcutsConfig,
	visibleShortcuts,
}: UseGlobalShortcutsTransformationPropsType): {
	shortcuts: GlobalShortcutsItem[];
	shortcutsWithMenuId: GlobalShortcutsConfigWithMenuId[];
} => {
	const { fireClickedSideNavItemEvent } = useNavAnalytics();
	// 1. Filter the shortcut config based on visible menu items
	const shortcutsWithMenuId = useMemo(() => {
		const availableShortcuts = visibleShortcuts.reduce(
			(acc: GlobalShortcutsConfigWithMenuId[], id: string) => {
				const shortcut = shortcutsConfig?.[id];
				if (shortcut) {
					acc.push({ menuItemId: id, ...shortcut });
				}
				return acc;
			},
			[],
		);

		if (growthShortcutsConfig) {
			availableShortcuts.push(
				...Object.entries<GlobalShortcutsItem>(growthShortcutsConfig).map(([id, shortcut]) => ({
					menuItemId: id,
					...shortcut,
				})),
			);
		}

		const availableShortcutsWithNonEmptyUrls = availableShortcuts.filter((shortcut) => {
			// If a shortcut has no resolvable URL, it may ultimately render nothing.
			// Filter those out so we can avoid rendering the whole GAS block when empty.
			if (shortcut.type === 'link' || shortcut.type === 'internal') {
				const url = getUrlMaybe(shortcut);
				return url !== undefined && url.trim() !== '';
			}

			// Component shortcuts can still render meaningful UI without a URL.
			return true;
		});

		return availableShortcutsWithNonEmptyUrls.map((shortcut) => ({
			...shortcut,
			onClick: () => {
				shortcut.onClick?.();
				fireClickedSideNavItemEvent(shortcut.menuItemId, 'postMain');
			},
			...(shortcut.type === 'link' && {
				onRightClick: () => {
					shortcut.onRightClick?.();
					fireClickedSideNavItemEvent(shortcut.menuItemId, 'postMain', true);
				},
			}),
		}));
	}, [visibleShortcuts, growthShortcutsConfig, shortcutsConfig, fireClickedSideNavItemEvent]);

	return {
		// eslint-disable-next-line no-unused-vars
		shortcuts: shortcutsWithMenuId.map(({ menuItemId, ...shortcut }) => shortcut) as any,
		shortcutsWithMenuId: shortcutsWithMenuId as any,
	};
};
